/**
 * RISKS-PREVENTION-ACTIONS.ROUTE
 * All risks and their prevention measures, highest residual risk first, then the RPS measures.
 */
import NavPrevention from "@/nav/nav.prevention"
import { useAppSelector } from "@/core/root-reducer"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"
import ListItem from "@/components/list-item"
import PreventionActionsFilterPanel from "@/entities/prevention-action/ui/PreventionActionsFilterPanel"
import PageHeader from "@/components/page-header"
import PhysicalRiskCard from "@/entities/physical-risk/ui/PhysicalRiskCard"
import preventionActionStore from "@/entities/prevention-action/model/prevention-action.store"
import populationStore from "@/entities/population/model/population.store"
import siteStore from "@/entities/site/model/site.store"
import RpsPreventionActionsCard from "@/entities/prevention-action/ui/RpsPreventionActionsCard"
import { isRpsPreventionActionForPopulation } from "@/entities/prevention-action/model/prevention-action.types"
import {
  groupMeasuresByRisk,
  matchesMeasuresView,
  PREVENTION_MEASURES_VIEWS,
  PreventionMeasuresView
} from "@/entities/prevention-action/model/prevention-action.utils"
import { useMemo } from "react"

export default function RisksPreventionActions({ view }: { view: PreventionMeasuresView }) {
  //Selectors
  const actions = useAppSelector(preventionActionStore.selectors.list)
  const risks = useAppSelector(physicalRiskStore.selectors.listFiltered)
  const selectedRiskIds = useAppSelector(physicalRiskStore.selectors.selected)
  const selectedSiteIds = useAppSelector(siteStore.selectors.selected)
  const selectedPopulationIds = useAppSelector(populationStore.selectors.selected)
  const rpsActions = useAppSelector((state) => preventionActionStore.selectors.listRps(state))

  const { title, subtitle } = PREVENTION_MEASURES_VIEWS[view]

  const actionsByRisk = useMemo(() => groupMeasuresByRisk(actions), [actions])

  const sortedRisks = useMemo(
    () =>
      risks
        .filter((risk) => selectedRiskIds.length === 0 || selectedRiskIds.includes(risk.id))
        .filter((risk) => matchesMeasuresView(actionsByRisk.get(risk.id) ?? [], view))
        .sort((a, b) => (b.cValueEffective ?? -1) - (a.cValueEffective ?? -1)),
    [risks, selectedRiskIds, actionsByRisk, view]
  )

  //Le RPS ne se filtre que par fonction : un filtre de risque ou de lieu le masque
  const filteredRpsActions = rpsActions.filter(
    (action) =>
      !selectedPopulationIds.length ||
      selectedPopulationIds.some((populationId) => isRpsPreventionActionForPopulation(action, populationId))
  )
  const isRpsVisible =
    !selectedRiskIds.length && !selectedSiteIds.length && matchesMeasuresView(filteredRpsActions, view)

  return (
    <NavPrevention>
      <div className="flex fixed top-[48px] left-0 h-[calc(100%-48px)] app-content-width">
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <div className="px-40 pt-20 [&>.page-header]:mb-0">
            <PageHeader section="Prévention" title={title} subtitle={subtitle} />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="flow-root px-[40px] pt-[12px] pb-[40px]">
              {sortedRisks.map((risk) => (
                <PhysicalRiskCard key={risk.id} risk={risk} measuresView={view} />
              ))}
              {isRpsVisible && (
                <RpsPreventionActionsCard
                  measuresView={view}
                  preventionActions={filteredRpsActions}
                  populationIds={selectedPopulationIds}
                />
              )}
              {!sortedRisks.length && !isRpsVisible && (
                <ListItem>
                  <div className="grey-t">Aucun risque ne correspond aux filtres sélectionnés.</div>
                </ListItem>
              )}
            </div>
          </div>
        </div>
        <PreventionActionsFilterPanel />
      </div>
    </NavPrevention>
  )
}
